import { buttonClass } from "@studyward/ui";
import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
  error: Error | null;
}

/**
 * Catches render errors and dynamic import / chunk load errors (e.g. when an in-memory client
 * requests an old chunk that was replaced by a new deployment). Auto-reloads once on chunk error.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isChunkError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    const msg = error?.message || "";
    const isChunkError =
      msg.includes("dynamically imported module") ||
      msg.includes("Loading chunk") ||
      msg.includes("Failed to fetch") ||
      error?.name === "TypeError";

    return { hasError: true, isChunkError, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
    if (this.state.isChunkError && typeof window !== "undefined") {
      const key = "sw_chunk_reload";
      const last = sessionStorage.getItem(key);
      const now = Date.now();
      if (!last || now - Number(last) > 10_000) {
        sessionStorage.setItem(key, String(now));
        window.location.reload();
      }
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  override render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-[50vh] flex-col items-center justify-center p-6 text-center">
          <div className="max-w-md rounded-2xl border border-border bg-surface-card p-6 shadow-card">
            <h2 className="text-h2 font-semibold text-text-primary">
              {this.state.isChunkError ? "App updated" : "Something went wrong"}
            </h2>
            <p className="mt-2 text-small text-text-secondary">
              {this.state.isChunkError
                ? "A new version of StudyWard is ready. Click below to load the latest features."
                : "An unexpected error occurred while loading this page."}
            </p>
            <div className="mt-5 flex justify-center gap-3">
              <button
                type="button"
                onClick={this.handleReload}
                className={buttonClass("primary", "sm")}
              >
                {this.state.isChunkError ? "Update now" : "Reload page"}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
